import { toast } from "react-toastify";
import Config from "../../../config/Config";
import QueueItemStatusInfo from "../../../models/viewModels/QueueItemModel";
import { ExcelUploadModal } from "../../modals/excelUploadModal/ExcelUploadModal";
import Download from "../../../core/DownloadUtil";
import { useAuth } from "react-oidc-context";
import { ButtonComponent } from "../../inputs/buttonComponent/ButtonComponent";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { QueueService } from "../../../services/QueueService";
import { Table } from "semantic-ui-react";
import { BulkUploadTooltipModal } from "../../modals/tooltipModal/TooltipModal";
import { LookupsContext } from "../../../lookups/LookupFactory";
import { QueueModal } from "../../modals/QueueModal/QueueModal";
import { ConfirmArchiveModal } from "../../modals/ConfirmArchiveModal/ConfirmArchiveModal";

export type ProjectUploadTabProps = {
    selectedJurisdictionId: number
}

export const ProjectUploadTab = (props: ProjectUploadTabProps) => {
    const auth = useAuth();

    const [lookups] = useContext(LookupsContext);
    const queueService = useMemo(() => new QueueService(auth.user?.access_token), [auth]);
    const [queueInfo, setQueueInfo] = useState<QueueItemStatusInfo[]>([]);
    const [tableLoading, setTableLoading] = useState<boolean>(false);

    const refreshTable = useCallback(() => {
        setTableLoading(true);
        queueService.getProjectUploadInfo(props.selectedJurisdictionId)
            .then((result) => {
                setQueueInfo(result.sort((a, b) => b.created.valueOf() < a.created.valueOf() ? -1 : 1));
            })
            .catch((error) => {
                console.error(error);
                toast.error("Error Retrieving Upload Info");
            })
            .finally(() => {
                setTableLoading(false);
            })
    }, [props.selectedJurisdictionId, queueService])

    useEffect(() => {
        refreshTable();
    }, [refreshTable])

    const downloadTemplateWcm = async () => {
        const endpoint = Config.api_endpoint + "BulkUploadDocument/WatershedControlMeasureTemplate";
        let toastId = toast.info("Downloading...", { containerId: "manage-project-data-toast-container" });

        try {
            await Download(endpoint, auth.user?.access_token, "watershed_control_measures_bulk_upload.xlsx");
        } catch (err) {
            toast.error("Failed to download file: " + err, { containerId: "manage-project-data-toast-container" });
        } finally {
            toast.dismiss(toastId);
        }
    }

    const downloadTemplateNar = async () => {
        const endpoint = Config.api_endpoint + "BulkUploadDocument/NewRedevelopmentTemplate";

        let toastId = toast.info("Downloading...", { containerId: "manage-project-data-toast-container" });
        try {
            await Download(endpoint, auth.user?.access_token, "new_redevelopment_bulk_upload.xlsx");
        } catch (err) {
            toast.error("Failed to download file: " + err, { containerId: "manage-project-data-toast-container" });
        } finally {
            toast.dismiss(toastId);
        }
    }

    const archiveBulkUpload = (queueItemId: number) => {
        queueService.archiveBulkUpload(queueItemId)
            .catch(() => {
                toast.error("Error Archiving");
            })
    }

    return <>
        <div className="queue-btn-wrapper">
            {/* UPLOADS PROJECTS BUTTON */}
            <div className="upload-button-and-instructions">
                <ExcelUploadModal
                    selectedJurisdictionId={props.selectedJurisdictionId}
                    refreshUploadList={refreshTable}
                />
                <BulkUploadTooltipModal />
            </div>
            <ButtonComponent
                onBtnClick={downloadTemplateWcm}
                label="Download Watershed Control Measures Template"
                theme="primaryBtn ml-1" />
            <ButtonComponent
                onBtnClick={downloadTemplateNar}
                label="Download New &amp; Redevelopment Template"
                theme="primaryBtn ml-1" />
            <ButtonComponent
                onBtnClick={() => refreshTable()}
                label="Refresh Table"
                disabled={tableLoading}
                loading={tableLoading}
                theme={"primaryBtn"} />
        </div>
        <div className="sub-header2">
            Recent Bulk Uploads <br />
            <div className="note">(Failed uploads can be archived from table)</div>
            <div className="note scroll-message">Hold shift while scrolling for horizontal table scroll</div>
        </div>
        <div className="project-upload-table">
            <Table celled fixed unstackable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width="2">File Name</Table.HeaderCell>
                        <Table.HeaderCell width="2">Upload Date</Table.HeaderCell>
                        <Table.HeaderCell width="1">Step</Table.HeaderCell>
                        <Table.HeaderCell width="1">Status</Table.HeaderCell>
                        <Table.HeaderCell width="2">Projects Uploaded Count</Table.HeaderCell>
                        <Table.HeaderCell width="1" className="center">Error(s) / Warning(s)</Table.HeaderCell>
                        <Table.HeaderCell width="2">Finish Date</Table.HeaderCell>
                        <Table.HeaderCell width="1" className="center">Archive</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {
                        queueInfo.map((item: QueueItemStatusInfo, index: number) => {
                            return (<Table.Row key={index + item.fileName}>
                                <Table.Cell>{item.fileName}</Table.Cell>
                                {/* Upload Date */}
                                <Table.Cell>{new Date(item.created).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</Table.Cell>
                                {/* Step */}
                                <Table.Cell>{lookups ? lookups.getQueueStepProcessById(item.queueStepProcessId) : "N/A"}</Table.Cell>
                                {/* Status */}
                                <Table.Cell>{lookups ? lookups.getQueueStatusById(item.queueStatusId) : "N/A"}</Table.Cell>
                                {/* Projects Uploaded Count */}
                                <Table.Cell className="projects-count-cell">{(item.totalProjects && item.queueStatusId === lookups.getQueueStatusByInternalName("complete")) ? (item.totalProjects + " (" + item.duplicateCount + " Duplicates)") : "N/A"}</Table.Cell>
                                {/* Errors / Warnings */}
                                <Table.Cell className="center">
                                    {(lookups.getQueueStatusById(item.queueStatusId) !== "Complete" || item.duplicateCount > 0 || item.warningCount > 0) &&
                                        <QueueModal queueItem={item} theme={"primary"} icon={"info circle"} queueError={item.queueError} lookups={lookups} refreshQueueList={refreshTable}/>}
                                </Table.Cell>
                                {/* Finish Date */}
                                <Table.Cell>{item.finishDate ? new Date(item.finishDate).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : "N/A"}</Table.Cell>
                                {/* Archive */}
                                <Table.Cell className="center">
                                    {lookups && item.queueStatusId === 1003
                                        ? <ConfirmArchiveModal theme={'blue'} icon='trash' displayName={item.fileName} confirmArchiveFunc={archiveBulkUpload} itemToArchive={item.id} />
                                        : ""
                                    }
                                </Table.Cell>
                            </Table.Row>);
                        })}
                </Table.Body>
            </Table>
        </div>
    </>;
}
